type BarChartProps = {
  values: number[];
  color?: string;
  height?: number;
  labels?: string[];
};

export const BarChart = ({
  values,
  color = "#1e3a5f",
  height = 120,
}: BarChartProps) => {
  const width = 320;
  const padding = { top: 8, right: 8, bottom: 20, left: 8 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const max = Math.max(...values, 1);
  const barGap = 2;
  const barWidth = values.length > 0
    ? (chartW - barGap * (values.length - 1)) / values.length
    : 0;

  if (values.length === 0) {
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
        <line
          x1={padding.left}
          y1={height / 2}
          x2={width - padding.right}
          y2={height / 2}
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      </svg>
    );
  }

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      {values.map((value, index) => {
        const barH = (value / max) * chartH;
        const x = padding.left + index * (barWidth + barGap);
        const y = padding.top + chartH - barH;

        return (
          <rect
            key={index}
            x={x}
            y={y}
            width={Math.max(barWidth, 1)}
            height={Math.max(barH, value > 0 ? 2 : 0)}
            fill={color}
            rx={1}
          />
        );
      })}
    </svg>
  );
};

type DualAxisChartProps = {
  barValues: number[];
  lineValues: number[];
  barColor?: string;
  lineColor?: string;
  height?: number;
};

export const DualAxisChart = ({
  barValues,
  lineValues,
  barColor = "#1e3a5f",
  lineColor = "#f97316",
  height = 120,
}: DualAxisChartProps) => {
  const width = 320;
  const padding = { top: 8, right: 8, bottom: 20, left: 8 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const barMax = Math.max(...barValues, 1);
  const lineMax = Math.max(...lineValues, 1);
  const count = Math.max(barValues.length, lineValues.length);
  const barGap = 2;
  const barWidth = count > 0 ? (chartW - barGap * (count - 1)) / count : 0;

  const linePoints = lineValues.map((value, index) => {
    const x = padding.left + index * (barWidth + barGap) + barWidth / 2;
    const y = padding.top + chartH - (value / lineMax) * chartH;

    return `${x},${y}`;
  });

  const linePath = linePoints.length > 0 ? `M ${linePoints.join(" L ")}` : "";

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      {barValues.map((value, index) => {
        const barH = (value / barMax) * chartH;
        const x = padding.left + index * (barWidth + barGap);
        const y = padding.top + chartH - barH;

        return (
          <rect
            key={`bar-${index}`}
            x={x}
            y={y}
            width={Math.max(barWidth, 1)}
            height={Math.max(barH, value > 0 ? 2 : 0)}
            fill={barColor}
            rx={1}
          />
        );
      })}
      {linePath ? (
        <path
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
};
